import React from 'react';
import {
  Container,
  Col,
  Row,
  Card,
  FormFeedback,
} from 'reactstrap';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useLocation, Link } from 'react-router-dom';
import { useFormik } from 'formik';

import {
  LOCATIONS,
  ROOM_TYPE,
} from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import {
  mergeStoredFacilities,
  toFacilitiesPayload,
  toFacilityGroups,
} from 'constants/roomFacility';
import { PARAM_SETTING } from 'constants/setting';

import AppBreadcrumb from 'components/App/Breadcrumb';
import AppButton from 'components/App/Button';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import AppMeta from 'components/App/Meta';
import Loading from 'components/App/Loading';
import RadioConvenience from 'components/RadioConvenience';
import SectionWrapper from 'components/AddLocation/SectionWrapper';
import SearchIcon from 'assets/images/search-icon.svg';

import { updateFacilities } from 'utils/validate';

import styles from './ConvenienceRoom.module.scss';

function ConvenienceRoom(props) {
  const {
    isLoadingAddRoomFacilities,
    isLoadingGetRoomFacilities,
    isLoadingGetSetting,

    roomFacilities,
    roomOfPlaceList,
    setting,

    actionGetSetting,
    actionGetListRoomsOfPlace,
    actionGetRoomFacilities,
    actionAddRoomFacilities,
  } = props;

  const [activeTab, setActiveTab] = React.useState('');
  const [searchTerm, setSearchTerm] = React.useState('');

  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');

  const breadcrumbList = [
    {
      name: 'Chỗ nghỉ',
      isActive: false,
      link: `${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${placeId}`,
    },
    {
      name: 'Tiện nghi phòng',
      isActive: true,
      link: '',
    },
  ];

  // The backend owns the catalogue; the page only renders what it answers (8 groups since the
  // 2026-09-28 rename). Form values stay keyed by group name, as RadioConvenience expects.
  const facilityGroups = React.useMemo(
    () => toFacilityGroups(setting[PARAM_SETTING.FACILITIES]),
    [setting],
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      roomsSize: [],
    },

    validationSchema: Yup.object(updateFacilities),

    onSubmit: (values) => {
      actionAddRoomFacilities({
        placeId,
        data: {
          roomsSize: values.roomsSize,
          // Every group with every child: the backend refuses a partial catalogue.
          facilities: toFacilitiesPayload(
            facilityGroups.map((group) => values[group.group] || group),
          ),
        },
      });
    },
  });

  const onChangeRoomSize = React.useCallback((roomId) => (e) => {
    validation.setValues((values) => ({
      ...values,
      roomsSize: values.roomsSize.map((room) => {
        if (room.room === roomId) {
          return {
            ...room,
            size: e.target.value || '',
          };
        }
        return room;
      }),
    }));
  }, [validation]);

  const onFindValueByRoomId = React.useCallback((roomId) => {
    const idx = validation.values.roomsSize.findIndex(
      (room) => room.room === roomId,
    );

    if (idx !== -1) {
      return validation.values.roomsSize[idx]?.size;
    }

    return 0;
  }, [validation]);

  React.useEffect(() => {
    if (placeId) {
      actionGetListRoomsOfPlace({ placeId });
      actionGetRoomFacilities({ placeId });
    }
  }, [actionGetListRoomsOfPlace, actionGetRoomFacilities, placeId]);

  React.useEffect(() => {
    // App boot already loads the settings; only a direct load of this page needs the catalogue.
    if (!setting[PARAM_SETTING.FACILITIES]) {
      actionGetSetting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Room sizes and facilities load from different requests but share one setValues: formik resolves
  // the updater against the values of the render it was called in, so a second call in the same
  // commit would drop what the first one wrote.
  React.useEffect(() => {
    const hasRooms = roomFacilities.rooms?.length > 0;

    if (!hasRooms && !facilityGroups.length) return;

    const merged = mergeStoredFacilities(
      facilityGroups,
      roomFacilities.roomFacilities,
      roomOfPlaceList.map((room) => room._id),
    );

    validation.setValues((values) => ({
      ...values,
      ...(hasRooms && {
        roomsSize: roomFacilities.rooms.map((room) => ({
          room: room._id,
          name: room.name,
          roomName: room.roomName,
          size: room.size || '0',
        })),
      }),
      ...Object.fromEntries(merged.map((group) => [group.group, group])),
    }));
  }, [facilityGroups, roomFacilities, roomOfPlaceList]);

  const activeGroup = activeTab || facilityGroups[0]?.group || '';

  const currentFacilityGroup = validation.values[activeGroup];

  const filteredFacilities = React.useMemo(() => {
    if (!currentFacilityGroup?.children) return [];
    if (!searchTerm.trim()) return currentFacilityGroup.children;

    const term = searchTerm.trim().toLowerCase();
    return currentFacilityGroup.children.filter((item) => (
      item.vi?.toLowerCase().includes(term)
      || item.en?.toLowerCase().includes(term)
    ));
  }, [currentFacilityGroup, searchTerm]);

  const getTabMatchCount = React.useCallback((tabKey) => {
    if (!searchTerm.trim()) return null;
    const group = validation.values[tabKey];
    if (!group?.children) return 0;
    const term = searchTerm.trim().toLowerCase();
    return group.children.filter((item) => (
      item.vi?.toLowerCase().includes(term)
      || item.en?.toLowerCase().includes(term)
    )).length;
  }, [validation.values, searchTerm]);

  return (
    <div className="page-content">
      <Loading loading={isLoadingGetRoomFacilities || isLoadingGetSetting} loaderOnPage />
      <div className={styles.Page}>
        <AppMeta
          name="Tiện nghi phòng"
        />

        <Container fluid>
          <AppBreadcrumb
            pageTitle="Tiện nghi phòng"
            breadcrumbList={breadcrumbList}
          />

          <div className="mb-24">
            <SectionWrapper
              title="Diện tích phòng"
            >
              <Row>
                {validation.values.roomsSize.map((room) => (
                  <Col xs="12" sm="6" md="4" key={room.room}>
                    <div className="Input__Wrapper">
                      <div className="Input__Title">
                        {room.name || ROOM_TYPE[room.roomName]?.label}
                      </div>

                      <AppInputNumberValidation
                        name={room.room}
                        isCustomOnChange
                        onChange={onChangeRoomSize(room.room)}
                        type="text"
                        placeholder="Nhập diện tích"
                        value={onFindValueByRoomId(room.room)}
                        validation={validation}
                        showCurrency="m²"
                      />
                    </div>
                  </Col>
                ))}
              </Row>
              {validation.errors.roomsSize && validation.touched.roomsSize && (
                <FormFeedback type="invalid" className="d-block font-size-12 mb-3">
                  {validation.errors.roomsSize}
                </FormFeedback>
              )}
            </SectionWrapper>
          </div>

          <Card className={styles.FacilityCard}>
            <div className={styles.CardHeader}>
              <h5 className={styles.CardTitle}>Tiện nghi phòng</h5>
            </div>

            <div className={styles.SearchContainer}>
              <img
                src={SearchIcon}
                alt="search"
                className={styles.SearchIcon}
              />
              <input
                type="text"
                className={styles.SearchInput}
                placeholder="Tìm kiếm tiện nghi..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className={styles.SearchClearBtn}
                  onClick={() => setSearchTerm('')}
                  aria-label="Xóa tìm kiếm"
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>

            <div className={styles.TabsContainer}>
              {facilityGroups.map((group) => {
                const matchCount = getTabMatchCount(group.group);
                return (
                  <button
                    key={group.group}
                    type="button"
                    className={`${styles.TabItem} ${activeGroup === group.group ? styles.TabItemActive : ''}`}
                    onClick={() => setActiveTab(group.group)}
                  >
                    <span>{group.vi}</span>
                    {matchCount !== null && (
                      <span className={styles.MatchBadge}>
                        (
                        {matchCount}
                        )
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className={styles.FacilityList}>
              {filteredFacilities?.length > 0 ? (
                filteredFacilities.map((item) => (
                  <div className={styles.FacilityItem} key={item.facility}>
                    <RadioConvenience
                      facilities={item}
                      roomOfPlaceList={roomFacilities.rooms}
                      group={activeGroup}
                      validation={validation}
                    />
                  </div>
                ))
              ) : (
                <div className={styles.EmptyState}>
                  <img src={SearchIcon} alt="search" style={{ width: '30px', height: '30px' }} />
                  <div className={styles.EmptyStateText}>
                    Không tìm thấy tiện nghi nào phù hợp
                    {searchTerm ? ` với "${searchTerm}"` : ''}
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card className={styles.SectionFooter}>
            <div className="d-flex justify-content-end" style={{ gap: '24px' }}>
              <Link
                to={{ pathname: LOCATIONS.LOCATION_DETAIL.DEFAULT, search: `?place=${placeId}` }}
                className="Button ButtonCancel"
              >
                Hủy
              </Link>

              <AppButton
                loading={isLoadingAddRoomFacilities}
                disabled={!facilityGroups.length}
                classes="Button ButtonAction"
                onClick={validation.handleSubmit}
              >
                Lưu
              </AppButton>
            </div>
          </Card>
        </Container>
      </div>
    </div>
  );
}

ConvenienceRoom.propTypes = {
  isLoadingAddRoomFacilities: PropTypes.bool.isRequired,
  isLoadingGetRoomFacilities: PropTypes.bool.isRequired,
  isLoadingGetSetting: PropTypes.bool.isRequired,

  roomFacilities: PropTypes.instanceOf(Object),
  roomOfPlaceList: PropTypes.instanceOf(Array),
  setting: PropTypes.instanceOf(Object),

  actionGetListRoomsOfPlace: PropTypes.func.isRequired,
  actionGetRoomFacilities: PropTypes.func.isRequired,
  actionAddRoomFacilities: PropTypes.func.isRequired,
  actionGetSetting: PropTypes.func.isRequired,
};

ConvenienceRoom.defaultProps = {
  roomFacilities: INITIAL_VALUE.OBJECT,
  roomOfPlaceList: INITIAL_VALUE.ARRAY,
  setting: INITIAL_VALUE.OBJECT,
};

export default ConvenienceRoom;
